import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck, Settings2, X } from "lucide-react";
import { getConsent, setConsent, acceptAll, declineAll } from "@/lib/consent";
import { isInternalUser } from "@/lib/internalTraffic";

/**
 * Consent banner — bottom card on desktop, bottom sheet on mobile.
 *
 * Two modes, decided by /lib/consent.js#getConsent():
 *
 *   - "opt-in" (EU / EEA / UK / GPC visitors)
 *     Analytics start denied. Banner asks the caregiver to Accept,
 *     Customize, or Decline. Standard GDPR flow.
 *
 *   - "opt-out" (US, rest of world)
 *     Analytics auto-grant on first visit (legitimate-interest model
 *     under CCPA/CPRA — no advertising trackers, no sale of data).
 *     Banner becomes a short cookie notice with an explicit "Decline"
 *     so caregivers can change their mind. This is the fix for the
 *     real bug we hit: US caregivers were never clicking "Accept", so
 *     Clarity never booted for them — only my and Emergent's QA
 *     sessions ever appeared in the dashboard.
 *
 * Hidden on the routes below — the banner overlapped the submit button
 * on small screens, and Playwright test runs were clicking the
 * banner's Privacy link instead of the login button. Users see it on
 * the next route after they sign in.
 *
 * Also hidden for any internal user (admin role, @emergent.sh, preview
 * host, manual opt-out) — teammates don't need a cookie notice and
 * shouldn't be flagged anyway.
 */
const HIDE_ON_ROUTES = ["/login", "/signup", "/try", "/beta-signup", "/private-beta-waitlist"];

export default function ConsentBanner() {
    const [open, setOpen] = useState(false);
    const [expand, setExpand] = useState(false);
    const [draft, setDraft] = useState({ analytics: true, clarity: true });
    const [autoGranted, setAutoGranted] = useState(false);
    const location = useLocation();

    useEffect(() => {
        // Never show the banner for internal traffic.
        if (isInternalUser()) {
            setOpen(false);
            return;
        }
        const c = getConsent();
        // Show the banner if:
        //   • the user hasn't explicitly decided yet (opt-in flow), OR
        //   • we auto-granted (opt-out flow) and they haven't confirmed.
        setOpen(!c.decided || !!c.autoGranted);
        setAutoGranted(!!c.autoGranted);
        setDraft({ analytics: c.analytics, clarity: c.clarity });
    }, []);

    if (!open) return null;
    if (HIDE_ON_ROUTES.some((r) => location.pathname.startsWith(r))) return null;

    const close = () => setOpen(false);

    const handleAccept = () => { acceptAll(); close(); };
    const handleDecline = () => { declineAll(); close(); };
    const handleConfirm = () => {
        // In auto-granted (opt-out) mode the primary CTA persists the
        // current draft so we stop showing the banner next visit.
        setConsent({ analytics: draft.analytics, clarity: draft.clarity });
        close();
    };
    const handleSave = () => {
        setConsent({ analytics: draft.analytics, clarity: draft.clarity });
        close();
    };

    return (
        <div
            role="dialog"
            aria-labelledby="ml-consent-title"
            aria-describedby="ml-consent-desc"
            data-testid="consent-banner"
            className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-3 sm:px-6 pb-3 sm:pb-6 pointer-events-none"
        >
            <div className="pointer-events-auto w-full max-w-2xl bg-white shadow-lg rounded-2xl border border-cream-dark overflow-hidden">
                <div className="p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <span className="shrink-0 rounded-full bg-teal-soft text-teal-dark p-2 mt-0.5">
                                <ShieldCheck size={18} strokeWidth={1.8} />
                            </span>
                            <div>
                                <p id="ml-consent-title" className="font-heading text-base sm:text-lg font-semibold text-[#1F2624]">
                                    {autoGranted ? "Privacy notice" : "A quick note on cookies"}
                                </p>
                                <p id="ml-consent-desc" className="text-sm text-[#4C5551] leading-relaxed mt-1">
                                    {autoGranted ? (
                                        <>
                                            Memory Lane uses privacy-respecting analytics (Google Analytics 4 and Microsoft Clarity)
                                            to understand where caregivers get stuck.
                                            No advertising trackers, no data sales — and we never record sensitive form fields.{" "}
                                            You can opt out any time below.{" "}
                                            <Link to="/privacy" className="text-teal-dark underline" data-testid="consent-link-privacy">Privacy Policy</Link>
                                            {" · "}
                                            <Link to="/cookies" className="text-teal-dark underline" data-testid="consent-link-cookies">Cookie Policy</Link>
                                        </>
                                    ) : (
                                        <>
                                            Memory Lane uses two privacy-respecting analytics tools — Google Analytics 4 and Microsoft Clarity —
                                            to understand how the app helps caregivers and where it gets in the way.
                                            No advertising trackers, no data sales. You can change your mind any time.{" "}
                                            <Link to="/privacy" className="text-teal-dark underline" data-testid="consent-link-privacy">Privacy Policy</Link>
                                            {" · "}
                                            <Link to="/cookies" className="text-teal-dark underline" data-testid="consent-link-cookies">Cookie Policy</Link>
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={close}
                            aria-label="Dismiss for now"
                            data-testid="consent-dismiss"
                            className="shrink-0 -m-2 p-2 text-[#6E7A74] hover:text-[#4C5551]"
                        >
                            <X size={18} strokeWidth={1.8} />
                        </button>
                    </div>

                    {expand && (
                        <div className="mt-4 space-y-2 border-t border-cream-light pt-4" data-testid="consent-customize">
                            <ConsentRow
                                label="Google Analytics 4"
                                helper="Aggregated traffic + conversion events. IP anonymized; no PII sent."
                                checked={draft.analytics}
                                onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
                                testid="consent-toggle-ga4"
                            />
                            <ConsentRow
                                label="Microsoft Clarity"
                                helper="Clarity is off on all health-data pages — it only runs on our informational pages."
                                checked={draft.clarity}
                                onChange={(v) => setDraft((d) => ({ ...d, clarity: v }))}
                                testid="consent-toggle-clarity"
                            />
                        </div>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-2 justify-end">
                        {!expand && (
                            <button
                                onClick={() => setExpand(true)}
                                data-testid="consent-customize-btn"
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark px-3 py-2 min-h-[40px]"
                            >
                                <Settings2 size={14} strokeWidth={1.8} /> Customize
                            </button>
                        )}
                        <button
                            onClick={handleDecline}
                            data-testid="consent-decline"
                            className="px-4 py-2 text-sm font-medium rounded-2xl border border-cream-dark text-[#4C5551] hover:border-teal hover:text-teal-dark min-h-[40px]"
                        >
                            {autoGranted ? "Opt out" : "Decline"}
                        </button>
                        {expand ? (
                            <button
                                onClick={handleSave}
                                data-testid="consent-save"
                                className="ml-btn-primary !min-h-[40px] !py-2 !px-4 text-sm"
                            >
                                Save preferences
                            </button>
                        ) : (
                            <button
                                onClick={autoGranted ? handleConfirm : handleAccept}
                                data-testid="consent-accept"
                                className="ml-btn-primary !min-h-[40px] !py-2 !px-4 text-sm"
                            >
                                {autoGranted ? "Got it" : "Accept"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ConsentRow({ label, helper, checked, onChange, testid }) {
    return (
        <label className="flex items-start gap-3 cursor-pointer py-1">
            <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-teal-dark"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                data-testid={testid}
            />
            <span className="flex-1">
                <span className="block text-sm font-medium text-[#1F2624]">{label}</span>
                <span className="block text-xs text-[#6E7A74] mt-0.5">{helper}</span>
            </span>
        </label>
    );
}
